import Modal from 'components/ui/Modal/modal';
import PrimaryButton from 'components/ui/PrimaryButton';
import SecondaryButton from 'components/ui/SecondaryButton';
import { useState } from 'react';
import { backendURL } from 'redux/reducers/settings';
import { useGetTraineeProfileList } from 'service/admin.query';
import * as XLSX from 'xlsx';

type Props = {
  routineId: number;
  open: boolean;
  toggle: () => void;
};

const EXPORT_CONFIG: any = {
  personal_details: {
    label: 'Personal Info',
    fields: {
      first_name: 'First Name',
      middle_nane: 'Middle Name',
      last_name: 'Last Name',
      gender: 'Gender',
      dob: 'DOB',
      citizen_ship_no: 'Citizenship No',
      bank_name: 'Bank',
      bank_account_number: 'A/C No',
      image: 'Image',
      recommendation_letter: 'Recommendation Letter'
    }
  },
  contact_details: {
    label: 'Contact Details',
    fields: {
      mobile_number: 'Mobile',
      email: 'Email'
    }
  },
  job_details: {
    label: 'Job Details',
    fields: {
      mode_of_entry: 'Entry Mode',
      batch: 'Batch',
      trainee_type: 'Type'
    }
  },
  extra_data: {
    label: 'Extra Info',
    fields: {
      attendance_pin_number: 'PIN',
      blood_group: 'Blood Group'
    }
  }
};

function DownloadTraineeProfile({ routineId, open, toggle }: Props) {
  const { data: traineeDetails = [], isLoading } = useGetTraineeProfileList(routineId, !!routineId);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    Object.keys(EXPORT_CONFIG)
  );

  const handleCheckboxChange = (id: string) => {
    setSelectedCategories((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const formatTraineeRow = (trainee: any, isExcel = false) => {
    let row: any = { 'Trainee ID': trainee.trainee_id };

    selectedCategories.forEach((catKey) => {
      const config = EXPORT_CONFIG[catKey];
      const data = trainee[catKey];
      if (!data) return;

      if (config.fields) {
        Object.keys(config.fields).forEach((fieldKey) => {
          const value = data[fieldKey];
          const excelHeader = config.fields[fieldKey];

          if ((fieldKey === 'image' || fieldKey === 'recommendation_letter') && value) {
            const fullUrl = backendURL + value;
            if (isExcel) {
              row[excelHeader] = { f: `HYPERLINK("${fullUrl}", "View File")` };
            } else {
              row[excelHeader] = (
                <a href={fullUrl} target="_blank" rel="noreferrer">
                  View File
                </a>
              );
            }
          } else if (fieldKey === 'gender') {
            row[excelHeader] = value == 1 ? 'Male' : value == 2 ? 'Female' : 'Others';
          } else {
            row[excelHeader] = value ?? '-';
          }
        });
      } else if (Array.isArray(data)) {
        // ... (keep your existing array logic)
      }
    });
    return row;
  };

  const handleDownload = () => {
    if (!traineeDetails.length) return;

    // Map data specifically for Excel
    const exportData = traineeDetails.map((t: any) => formatTraineeRow(t, true));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Trainees');

    XLSX.writeFile(workbook, `Trainee_Report_${routineId}.xlsx`);
  };

  // Generate Headers for the Table based on selection
  const getTableHeaders = () => {
    const headers: string[] = ['Trainee ID'];
    selectedCategories.forEach((catKey) => {
      const config = EXPORT_CONFIG[catKey];
      if (config.fields) {
        headers.push(...(Object.values(config.fields) as string[]));
      } else {
        headers.push(config.label);
      }
    });
    return headers;
  };

  const tableHeaders = getTableHeaders();

  return (
    <Modal
      title="Download Trainee Profile"
      isModalOpen={open}
      closeModal={toggle}
      modalSize="modal-xl"
      renderFooter={() => (
        <div className="hstack gap-2 justify-content-end">
          <SecondaryButton onClick={toggle} title="Close" />
          <PrimaryButton
            type="button"
            title="Download Excel"
            iconName="ri-file-excel-2-line me-1"
            onClick={handleDownload}
            isLoading={isLoading}
            disabled={selectedCategories.length === 0 || traineeDetails.length === 0}
          />
        </div>
      )}>
      <div className="p-3">
        {/* Category Selection */}
        <div className="mb-4">
          <label className="fw-bold mb-2">Select Groups to include:</label>
          <div className="row bg-light p-3 rounded border g-3">
            <div className="col-12 border-bottom pb-2">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={selectedCategories.length === Object.keys(EXPORT_CONFIG).length}
                  onChange={(e) =>
                    setSelectedCategories(e.target.checked ? Object.keys(EXPORT_CONFIG) : [])
                  }
                />
                <label className="form-check-label fw-bold">Select All Groups</label>
              </div>
            </div>
            {Object.keys(EXPORT_CONFIG).map((key) => (
              <div key={key} className="col-md-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`check-${key}`}
                    checked={selectedCategories.includes(key)}
                    onChange={() => handleCheckboxChange(key)}
                  />
                  <label className="form-check-label" htmlFor={`check-${key}`}>
                    {EXPORT_CONFIG[key].label}
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dynamic Preview Table */}
        <div className="mb-2 fw-bold">Data Preview (Top 5):</div>
        <div className="table-responsive border rounded" style={{ maxHeight: '400px' }}>
          <table className="table table-sm table-hover mb-0 text-nowrap">
            <thead className="table-secondary sticky-top">
              <tr>
                {tableHeaders.map((header, i) => (
                  <th key={i} className="small">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={tableHeaders.length} className="text-center p-3">
                    Loading data...
                  </td>
                </tr>
              ) : traineeDetails.length === 0 ? (
                <tr>
                  <td colSpan={tableHeaders.length} className="text-center p-3">
                    No trainees found for this routine.
                  </td>
                </tr>
              ) : (
                traineeDetails.slice(0, 5).map((trainee: any, idx: number) => {
                  const formattedRow = formatTraineeRow(trainee);
                  return (
                    <tr key={idx}>
                      {tableHeaders.map((header, i) => (
                        <td key={i} className="small">
                          {formattedRow[header] || ''}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="p-2 bg-light text-center small text-muted border border-top-0 rounded-bottom">
          {traineeDetails.length} total records will be exported.
        </div>
      </div>
    </Modal>
  );
}

export default DownloadTraineeProfile;
